import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { finalize } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import {
  AnalyseResultItem,
  AntageneUploadService,
} from '../../../services/antagene-upload.service';

interface AnalysisRowView {
  id: number;
  hashLabel: string;
  dateLabel: string;
  fichiersDepart: Array<{ label: string; href: string | null }>;
  typePipeLabel: string;
  methodeTriLabel: string;
  cartouchesLabel: string;
  departementsLabel: string;
  fichierResultatLabel: string;
  fichierResultatHref: string | null;
  cartoucheResultats: Array<{
    fichierResultatLabel: string;
    cartoucheLabel: string;
    fichierDepartLabel: string;
    href: string | null;
  }>;
  typePipeNom: string;
}

@Component({
  selector: 'app-resultat-analyses',
  imports: [CommonModule],
  templateUrl: './resultat-analyses.component.html',
  styleUrls: ['./resultat-analyses.component.scss']
})
export class ResultatAnalysesComponent implements OnInit {
  protected loading = false;
  protected downloadingResultsZip = false;
  protected error = '';
  protected items: AnalysisRowView[] = [];
  protected searchTerm = '';
  protected currentPage = 1;
  protected totalCount = 0;
  protected totalPages = 0;
  protected readonly pageSize = 25;
  protected deletingAnalyseIds: Set<number> = new Set();
  private readonly baseUrl = `${environment.apiBaseUrl}/api/`;

  constructor(
    private readonly antageneUploadService: AntageneUploadService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.load();
  }

  protected refresh(): void {
    this.load(this.currentPage);
  }

  protected applySearch(searchTerm: string): void {
    this.searchTerm = searchTerm.trim();
    this.load(1);
  }

  protected clearSearch(searchInput: HTMLInputElement): void {
    searchInput.value = '';
    this.searchTerm = '';
    this.load(1);
  }

  protected previousPage(): void {
    if (this.currentPage > 1 && !this.loading) {
      this.load(this.currentPage - 1);
    }
  }

  protected nextPage(): void {
    if (this.currentPage < this.totalPages && !this.loading) {
      this.load(this.currentPage + 1);
    }
  }

  protected downloadFile(filename: string): void {
    const link = document.createElement('a');
    link.href = `${this.baseUrl}gestion-pipe/antagene/download/${encodeURIComponent(filename)}`;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  protected downloadAllResultFilesZip(): void {
    if (this.downloadingResultsZip) {
      return;
    }

    this.downloadingResultsZip = true;
    this.error = '';
    this.cdr.markForCheck();

    this.antageneUploadService.downloadResultsZip({
      include_all_results: true,
      search: this.searchTerm,
    }).subscribe({
      next: (blob: Blob) => {
        this.downloadBlob(blob, this.buildResultZipFilename());
        this.downloadingResultsZip = false;
        this.cdr.markForCheck();
      },
      error: (err: HttpErrorResponse) => {
        this.error = `Erreur lors du téléchargement ZIP : ${this.getErrorMessage(err)}`;
        this.downloadingResultsZip = false;
        this.cdr.markForCheck();
      }
    });
  }

  protected deleteAnalyse(analyseId: number): void {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette analyse et ses fichiers associés ?')) {
      return;
    }

    this.deletingAnalyseIds.add(analyseId);
    this.cdr.markForCheck();

    this.antageneUploadService.deleteAnalyse(analyseId).subscribe({
      next: (response) => {
        this.deletingAnalyseIds.delete(analyseId);
        this.error = '';
        this.load(this.currentPage);
        this.cdr.markForCheck();
      },
      error: (err: HttpErrorResponse) => {
        this.deletingAnalyseIds.delete(analyseId);
        this.error = `Erreur lors de la suppression : ${this.getErrorMessage(err)}`;
        this.cdr.markForCheck();
      }
    });
  }

  protected get pipeLabel(): string {
    return '';
  }

  private load(page = 1): void {
    this.loading = true;
    this.error = '';
    this.cdr.markForCheck();

    this.antageneUploadService.listAnalyses(page, this.pageSize, this.searchTerm)
      .pipe(finalize(() => {
        this.loading = false;
        this.cdr.markForCheck();
      }))
      .subscribe({
        next: (response) => {
          this.items = (response.items ?? []).map((item) => this.toView(item));
          this.currentPage = response.page ?? page;
          this.totalCount = response.count ?? 0;
          this.totalPages = this.totalCount > 0 ? Math.ceil(this.totalCount / this.pageSize) : 0;
          this.cdr.markForCheck();
        },
        error: (err: HttpErrorResponse) => {
          this.items = [];
          this.totalCount = 0;
          this.totalPages = 0;
          this.error = this.getErrorMessage(err);
          this.cdr.markForCheck();
        }
      });
  }

  private toView(item: AnalyseResultItem): AnalysisRowView {
    const typePipeNom = String(item.type_pipe_nom ?? '').trim();
    const isAntagene = this.isAntagene(item);
    const cartouches = item.cartouches ?? [];
    const cartouchesLabel = cartouches.map((cartouche) => cartouche.cartouche_nom).filter(Boolean).join(', ');
    const departementsLabel = (item.departements ?? []).join(', ');
    const dateLabel = this.formatDate(item.date_analyse);
    const fichiersDepartNames = (item.fichiers_depart ?? [])
      .map((value) => String(value ?? '').trim())
      .filter((value) => !!value);
    const fichierResultatLabel = isAntagene ? (String(item.fichier_resultat ?? 'N/A') || 'N/A') : '-';
    const fichierResultatHref = isAntagene && item.fichier_resultat ? this.getDownloadHref(item.fichier_resultat) : null;

    return {
      id: item.id,
      hashLabel: String(item.type_hash ?? '-'),
      dateLabel,
      fichiersDepart: fichiersDepartNames.map((label) => ({
        label,
        href: this.getDownloadHref(label),
      })),
      typePipeLabel: typePipeNom || String(item.type_pipe_id),
      methodeTriLabel: this.formatMethodeTri(item.methode_tri),
      cartouchesLabel,
      departementsLabel,
      fichierResultatLabel,
      fichierResultatHref,
      cartoucheResultats: isAntagene
        ? cartouches.map((cartouche) => ({
            fichierResultatLabel: String(cartouche.fichier_resultat ?? '-').trim() || '-',
            cartoucheLabel: String(cartouche.cartouche_nom ?? '-'),
            fichierDepartLabel: String(cartouche.fichier_depart ?? '-'),
            href: cartouche.fichier_resultat ? this.getDownloadHref(cartouche.fichier_resultat) : null,
          }))
        : [],
      typePipeNom: typePipeNom.toLowerCase(),
    };
  }

  private getDownloadHref(filename: string): string {
    return `${this.baseUrl}gestion-pipe/antagene/download/${encodeURIComponent(filename)}`;
  }

  private downloadBlob(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  private buildResultZipFilename(): string {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    return `resultats_analyses_antagene_${yyyy}${mm}${dd}.zip`;
  }

  private formatDate(dateValue?: string | null): string {
    if (!dateValue) {
      return '-';
    }

    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) {
      return '-';
    }

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  }

  private formatMethodeTri(value: string): string {
    const normalized = String(value ?? '').trim().toLowerCase();
    if (normalized === 'pln_tb') {
      return 'PLN+TB';
    }
    if (normalized === 'pln') {
      return 'PLN';
    }
    if (normalized === 'tb') {
      return 'TB';
    }
    return normalized || '-';
  }

  private isAntagene(item: AnalyseResultItem): boolean {
    return String(item.type_pipe_nom ?? '').trim().toLowerCase() === 'antagene';
  }

  private getErrorMessage(err: HttpErrorResponse): string {
    if (typeof err.error === 'string' && err.error.trim()) {
      return err.error;
    }
    if (err.error?.detail) {
      return typeof err.error.detail === 'string' ? err.error.detail : JSON.stringify(err.error.detail);
    }
    if (err.error?.message) {
      return String(err.error.message);
    }
    return err.statusText || 'Erreur inconnue';
  }
}
